import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Phone, Save, CheckCircle2, XCircle, Loader2, Clock, Filter, MessageCircle, User,
} from 'lucide-react';

/**
 * PhoneVerificationsPage — admin queue of phone verification requests.
 *
 * Two panels:
 *   (1) Settings: admin's WhatsApp number shown to players.
 *   (2) Queue: pending requests with approve/reject; tab switches to approved/rejected.
 */
export default function PhoneVerificationsPage() {
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [queue, setQueue] = useState({ items: [], counts: {} });
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState({});
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadAll = async () => {
    try {
      const [sRes, qRes] = await Promise.all([
        api.get('/api/admin/phone-verifications/settings'),
        api.get(`/api/admin/phone-verifications${status ? `?status=${status}` : ''}`),
      ]);
      setSettings(sRes.data);
      setSettingsDraft((d) => (d == null ? sRes.data : d));
      setQueue(qRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const saveSettings = async () => {
    if (!settingsDraft?.whatsapp_number || settingsDraft.whatsapp_number.length < 5) {
      toast.error('WhatsApp number is required');
      return;
    }
    setSavingSettings(true);
    try {
      const r = await api.put('/api/admin/phone-verifications/settings', settingsDraft);
      setSettings(r.data);
      toast.success('Verification settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const approve = async (id) => {
    setActioning((a) => ({ ...a, [id]: 'approve' }));
    try {
      await api.post(`/api/admin/phone-verifications/${id}/approve`);
      toast.success('Verification approved');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed');
    } finally {
      setActioning((a) => ({ ...a, [id]: null }));
    }
  };

  const reject = async () => {
    if (!rejectModal) return;
    const id = rejectModal;
    setActioning((a) => ({ ...a, [id]: 'reject' }));
    try {
      await api.post(`/api/admin/phone-verifications/${id}/reject`, { reason: rejectReason });
      toast.success('Verification rejected');
      setRejectModal(null);
      setRejectReason('');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reject failed');
    } finally {
      setActioning((a) => ({ ...a, [id]: null }));
    }
  };

  const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtFP = (v) => `${Math.round(Number(v || 0)).toLocaleString()} FP`;
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="phone-verifications-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Phone className="w-6 h-6 text-[#25D366]" /> Verify Phone Numbers
        </h1>
        <p className="text-xs text-gray-500 mt-1">
          One-time verification before a player's first withdrawal. Set your WhatsApp number
          below; players see a popup guiding them to text you, then submit a request here for approval.
        </p>
      </div>

      {/* ── Settings panel ── */}
      <div className="rounded-xl p-4 border border-white/10 bg-[#131822] space-y-3"
           data-testid="verify-settings-panel">
        <div className="flex items-center gap-2 text-sm font-bold text-white">
          <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp Settings
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">WhatsApp number (with country code)</label>
            <input
              type="tel"
              placeholder="+1 555 123 4567"
              value={settingsDraft?.whatsapp_number ?? ''}
              onChange={(e) => setSettingsDraft((d) => ({ ...(d || {}), whatsapp_number: e.target.value }))}
              className="w-full px-3 py-2 bg-[#0f1320] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#25D366]"
              data-testid="verify-settings-number-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-400 mb-1">Pre-filled message (use <code className="text-[10px] bg-black/40 px-1 rounded">{'{username}'}</code> / <code className="text-[10px] bg-black/40 px-1 rounded">{'{email}'}</code>)</label>
            <textarea
              rows={2}
              placeholder="Hi, verifying my phone for withdrawals. I'm {username} ({email})."
              value={settingsDraft?.whatsapp_message_template ?? ''}
              onChange={(e) => setSettingsDraft((d) => ({ ...(d || {}), whatsapp_message_template: e.target.value }))}
              className="w-full px-3 py-2 bg-[#0f1320] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#25D366] resize-none"
              data-testid="verify-settings-template-input"
            />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settingsDraft?.enabled}
              onChange={(e) => setSettingsDraft((d) => ({ ...(d || {}), enabled: e.target.checked }))}
              data-testid="verify-settings-enabled-toggle"
            />
            Enable phone-verification gate for first-time withdrawals
          </label>
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-[#0B0E14]"
            style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)' }}
            data-testid="verify-settings-save-btn"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>

      {/* ── Queue ── */}
      <div className="flex items-center gap-2 text-xs">
        {['pending', 'approved', 'rejected'].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`verify-tab-${s}`}
            className={`px-3 py-1.5 rounded-full border capitalize ${
              status === s
                ? 'bg-[#25D366]/20 border-[#25D366] text-green-300'
                : 'bg-[#131822] border-white/10 text-gray-400 hover:text-gray-200'
            }`}
          >
            {s} ({queue.counts?.[s] ?? 0})
          </button>
        ))}
        <span className="text-gray-600 text-[10px] ml-2 flex items-center gap-1"><Filter className="w-3 h-3" /> status filter</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 p-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : queue.items.length === 0 ? (
        <div className="rounded-xl border border-white/10 p-10 text-center text-gray-500"
             data-testid="verify-queue-empty">
          No {status} verifications.
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden"
             data-testid="verify-queue-table">
          <table className="w-full text-sm">
            <thead className="bg-[#131822] text-gray-400 text-xs">
              <tr>
                <th className="text-left px-3 py-2">Player</th>
                <th className="text-left px-3 py-2">Phone</th>
                <th className="text-right px-3 py-2">Cash</th>
                <th className="text-right px-3 py-2">FP</th>
                <th className="text-left px-3 py-2">Submitted</th>
                {status !== 'pending' && <th className="text-left px-3 py-2">Reviewed</th>}
                {status === 'rejected' && <th className="text-left px-3 py-2">Reason</th>}
                {status === 'pending' && <th className="text-center px-3 py-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {queue.items.map((v) => (
                <tr key={v.id} className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`verify-row-${v.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{v.player_username || '—'}</div>
                        <div className="text-[11px] text-gray-500">{v.player_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-200 tabular-nums">{v.phone}</td>
                  <td className="px-3 py-2 text-right text-[#F5D76E] tabular-nums">{fmt(v.wallet?.cash_balance)}</td>
                  <td className="px-3 py-2 text-right text-[#C7D2FE] tabular-nums">{fmtFP(v.wallet?.fp_balance)}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{fmtDate(v.submitted_at)}</td>
                  {status !== 'pending' && (
                    <td className="px-3 py-2 text-gray-400 text-xs">
                      <div>{fmtDate(v.reviewed_at)}</div>
                      <div className="text-[10px] text-gray-600">by {v.reviewed_by || '—'}</div>
                    </td>
                  )}
                  {status === 'rejected' && (
                    <td className="px-3 py-2 text-xs text-red-300">{v.rejection_reason || '—'}</td>
                  )}
                  {status === 'pending' && (
                    <td className="px-3 py-2 text-center">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => approve(v.id)}
                          disabled={!!actioning[v.id]}
                          className="px-3 py-1 rounded-md text-[11px] bg-green-500/20 text-green-300 hover:bg-green-500/30 disabled:opacity-40 flex items-center gap-1"
                          data-testid={`verify-approve-${v.id}`}
                        >
                          {actioning[v.id] === 'approve'
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <CheckCircle2 className="w-3 h-3" />} Approve
                        </button>
                        <button
                          onClick={() => { setRejectModal(v.id); setRejectReason(''); }}
                          disabled={!!actioning[v.id]}
                          className="px-3 py-1 rounded-md text-[11px] bg-red-500/20 text-red-300 hover:bg-red-500/30 disabled:opacity-40 flex items-center gap-1"
                          data-testid={`verify-reject-${v.id}`}
                        >
                          <XCircle className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Reject reason modal ── */}
      {rejectModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-3"
          onClick={() => setRejectModal(null)}
          data-testid="verify-reject-modal"
        >
          <div className="w-full md:w-[420px] rounded-xl bg-[#131822] border border-red-500/30 p-5 space-y-3"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white">Reject verification</h3>
            <textarea
              rows={3}
              placeholder="Reason (shown to the player, optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-3 py-2 bg-[#0f1320] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-red-500 resize-none"
              data-testid="verify-reject-reason-input"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setRejectModal(null)}
                className="flex-1 py-2 rounded-lg border border-white/10 text-sm text-gray-300 hover:bg-white/5"
                data-testid="verify-reject-cancel"
              >Cancel</button>
              <button
                onClick={reject}
                className="flex-1 py-2 rounded-lg text-sm font-bold text-white bg-red-500 hover:bg-red-600"
                data-testid="verify-reject-confirm"
              >Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
